import React, { forwardRef, useRef, useContext } from 'react';
import { getContext, useCombinedRefs } from 'common';
import { SliderContext } from 'components/Slider';
import Styled from './styles';

export interface TeardropProps extends React.ComponentPropsWithRef<'div'> {
  /** Props passed from internal Slider. */
  internalProps: any;
  /** Props which will be passed to wrapper element */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** Props for the container of the thumb value. */
  textContainerRef?: React.RefObject<HTMLElement>;
}

/** Single Teardrop appearing on top of slider thumb.
 * All additional props will be forwarded to the root element.
 * When it is used, it should be passed as value of `teardropContent` property in `thumbProps` prop for Slider
 */
const Teardrop = forwardRef<HTMLElement, TeardropProps>((props: TeardropProps, ref) => {
  const isRTL = getContext(({ RTL }) => RTL);
  const {
    internalProps,
    wrapperProps,
    textContainerRef,
    ...otherProps
  } = props;
  const { disableAnimation } = useContext(SliderContext);
  const { style, ...otherWrapperProps } = wrapperProps || {};

  const wrapperRef = useRef();
  const combinedWrapperRef = useCombinedRefs(ref, wrapperRef);

  const innerTextContainerRef = useRef();
  const combinedTextContainerRef = useCombinedRefs(textContainerRef, innerTextContainerRef);

  const {
    value,
    vertical,
    isTicker,
    disabled,
    pos,
    flipIndicatorPosition,
    alwaysShowTeardrop,
    isThumbHover,
    thumbPressed,
    trackLength,
    $theme,
    renderMinLabel,
    renderMaxLabel,
    isRangeSelection
  } = internalProps;


  // In rangeSelection mode, when text is long, keep the text in the range of slider when thumb is at the end of track.
  const getRangeThumbPosition = () => {
    if (combinedTextContainerRef.current && !vertical && isRangeSelection) {
      const thumbWrapperFocusBorderWidth = 2;
      const thumbWrapperWidth = combinedWrapperRef.current.parentElement.getBoundingClientRect().width;
      // thumb will go over the track at the end for half of his width.
      const halfTotalThumbWidth = (thumbWrapperFocusBorderWidth + thumbWrapperWidth) / 2;
      const halfTearWrapperWidth = combinedTextContainerRef.current.getBoundingClientRect().width / 2;

      const thumbToLeftEnd = (pos + halfTotalThumbWidth);
      const thumbToRightEnd = (trackLength - pos) + halfTotalThumbWidth;

      if (thumbToLeftEnd < halfTearWrapperWidth) {
        return { left: -pos };
      }
      if (thumbToRightEnd < halfTearWrapperWidth) {
        const delta = pos - trackLength;
        return { right: delta };
      }
    }
    return { left: 'auto' };
  };

  // hide the thumb indicator value when slider is using default labels
  const shouldShowValue = () => {
    if (disabled) return false;
    if (vertical || alwaysShowTeardrop) return true;
    if ((pos === 0 && !renderMinLabel) || (pos === trackLength && !renderMaxLabel)) {
      return false;
    }
    return true;
  };

  if (!internalProps) return null;

  return (
    <Styled.TearWrapper
      ref={combinedWrapperRef}
      $rtl={isRTL}
      flipIndicatorPosition={flipIndicatorPosition}
      $disableAnimation={disableAnimation}
      isTicker={isTicker}
      isThumbHover={isThumbHover}
      thumbPressed={thumbPressed}
      vertical={vertical}
      alwaysShowTeardrop={isTicker && alwaysShowTeardrop}
      style={{ ...getRangeThumbPosition(), ...style }}
      {...otherWrapperProps}
    >
      <Styled.DefaultTeardrop
        $rtl={isRTL}
        $theme={$theme}
        disabled={disabled}
        tabIndex={!isTicker ? -1 : 0}
        isTicker={isTicker}
        vertical={vertical}
        flipIndicatorPosition={flipIndicatorPosition}
        {...otherProps}
      >
        {shouldShowValue() && <Styled.ValueWrapper ref={combinedTextContainerRef}>{value}</Styled.ValueWrapper>}
      </Styled.DefaultTeardrop>
    </Styled.TearWrapper>);
});

Teardrop.defaultProps = {
  wrapperProps: {},
  textContainerRef: undefined
};

Teardrop.displayName = 'Teardrop';

export default Teardrop;
